import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { MonitoringProductItem } from "@project/monitoring-product-item";

export type DeviceSlotRole = "head" | "sensor" | "licence";

interface PatientMonitorCreateOptions {
  family?: MonitoringProductItem;
  sensor?: MonitoringProductItem;
  mountingGroup?: MonitoringProductItem;
  mountingArm?: MonitoringProductItem;
  attachments?: MonitoringProductItem[];
}

interface CentralStationCreateOptions {
  base?: MonitoringProductItem;
  licence: MonitoringProductItem;
  additionalLicences?: MonitoringProductItem[];
  accessories?: MonitoringProductItem[];
  baseOptions?: MonitoringProductItem[];
}

interface KvmCreateOptions {
  familyNumber: string;
  family: MonitoringProductItem;
  transmitter: MonitoringProductItem;
  receiver?: MonitoringProductItem;
  accessories?: MonitoringProductItem[];
}

interface TelemetryCreateOptions {
  family?: MonitoringProductItem[];
  sensor?: MonitoringProductItem;
  additionalBatteries?: MonitoringProductItem[];
}

interface DisplayCreateOptions {
  display: MonitoringProductItem;
  accessories?: MonitoringProductItem[];
}

interface UtilityCreateOptions {
  devices: MonitoringProductItem[];
}

interface FromSearchResultParams {
  category: MonitoringCategory;
  family: Family;
  item: MonitoringProductItem;
  name: string;
  project: MonitoringProject;
  slotRole?: DeviceSlotRole;
}

type CategoryHandler = (params: FromSearchResultParams) => ProductConfiguration;

export class MonitoringConfigurationFactory {
  private static readonly categoryHandlers: Partial<Record<MonitoringCategory, CategoryHandler>> = {
    [MonitoringCategory.PATIENT_MONITORS]: ({ name, family, project, item }) =>
      MonitoringConfigurationFactory.createPatientMonitor(name, family, project, {
        family: item,
        sensor: item,
      }),

    [MonitoringCategory.CENTRAL_STATIONS]: ({ name, family, project, item }) =>
      MonitoringConfigurationFactory.createCentralStation(name, family, project, {
        base: item,
        licence: item,
      }),

    [MonitoringCategory.TELEMETRY]: ({ name, family, project, item, slotRole }) =>
      MonitoringConfigurationFactory.createTelemetry(name, family, project, {
        family: [item],
        sensor: slotRole === "sensor" ? item : undefined,
      }),

    [MonitoringCategory.KVMS]: ({ name, family, project, item }) =>
      MonitoringConfigurationFactory.createKvm(name, project, {
        familyNumber: family.familyNumber,
        family: item,
        transmitter: item,
      }),

    [MonitoringCategory.DISPLAYS]: ({ name, family, project, item }) =>
      MonitoringConfigurationFactory.createDisplay(name, family, project, {
        display: item,
      }),

    [MonitoringCategory.UTILITY]: ({ name, family, project, item }) =>
      MonitoringConfigurationFactory.createUtility(name, family, project, {
        devices: [item],
      }),
  };

  static createPatientMonitor(
    name: string,
    family: Family,
    project: MonitoringProject,
    options: PatientMonitorCreateOptions,
  ): PatientMonitorConfiguration {
    return PatientMonitorConfiguration.create({
      name,
      project,
      familyNumber: family.familyNumber,
      category: MonitoringCategory.PATIENT_MONITORS,
      family: options.family,
      sensor: options.sensor,
      mountingGroup: options.mountingGroup,
      mountingArm: options.mountingArm,
      attachments: options.attachments,
    });
  }

  static createCentralStation(
    name: string,
    family: Family,
    project: MonitoringProject,
    options: CentralStationCreateOptions,
  ): CentralStationConfiguration {
    return CentralStationConfiguration.create(
      name,
      project,
      family.familyNumber,
      MonitoringCategory.CENTRAL_STATIONS,
      options.base,
      options.licence,
      options.additionalLicences ?? [],
      options.accessories ?? [],
      [],
      options.baseOptions,
    );
  }

  static createKvm(name: string, project: MonitoringProject, options: KvmCreateOptions): KvmConfiguration {
    return KvmConfiguration.create(
      name,
      project,
      options.familyNumber,
      MonitoringCategory.KVMS,
      options.family,
      options.transmitter,
      options.receiver,
      options.accessories,
    );
  }

  static createTelemetry(
    name: string,
    family: Family,
    project: MonitoringProject,
    options: TelemetryCreateOptions,
  ): TelemetryConfiguration {
    return TelemetryConfiguration.create(
      name,
      project,
      family.familyNumber,
      MonitoringCategory.TELEMETRY,
      options.family,
      options.sensor,
      options.additionalBatteries,
    );
  }

  static createDisplay(
    name: string,
    family: Family,
    project: MonitoringProject,
    options: DisplayCreateOptions,
  ): DisplayConfiguration {
    return DisplayConfiguration.create(
      name,
      project,
      family.familyNumber,
      MonitoringCategory.DISPLAYS,
      options.display,
      options.accessories,
    );
  }

  static createUtility(
    name: string,
    family: Family,
    project: MonitoringProject,
    options: UtilityCreateOptions,
  ): UtilityConfiguration {
    return UtilityConfiguration.create(name, project, family.familyNumber, MonitoringCategory.UTILITY, options.devices);
  }

  static fromSearchResult(
    category: MonitoringCategory,
    family: Family,
    option: FamilyStepOption,
    name: string,
    project: MonitoringProject,
    slotRole: DeviceSlotRole = "head",
  ): ProductConfiguration {
    const handler = this.categoryHandlers[category];

    if (!handler) {
      throw new Error(`Unsupported monitoring category: ${category}`);
    }

    return handler({
      category,
      family,
      item: MonitoringProductItem.create(option, 1),
      name,
      project,
      slotRole,
    });
  }
}
